import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../lib/apiBase';

const AuthContext = createContext(null);

const API = `${API_BASE}/api`;

// Diagnostic: print effective API base URL at app startup once per page load
// (helps debug production regressions where REACT_APP_BACKEND_URL gets baked
// into the build with a cross-origin value)
if (typeof window !== 'undefined' && !window.__owntune_auth_boot_logged) {
    // eslint-disable-next-line no-console
    console.log(
        '[Auth boot]',
        {
            effective_api_base: API_BASE || '(same-origin)',
            env_raw: API_BASE || '(empty)',
            window_host: window.location.host,
            has_token_in_localStorage: !!localStorage.getItem('token'),
        }
    );
    window.__owntune_auth_boot_logged = true;
}

// All localStorage/sessionStorage keys that auth touches.
const AUTH_STORAGE_KEYS = {
    localStorage: ['token', 'user', 'auth_token', 'jwt', 'session', 'owntune_token', 'guest_session'],
    sessionStorage: ['originalAdminToken', 'impersonation', 'session_id'],
};

/**
 * Purge all auth-related keys from storage.
 * Called ONLY by: explicit logout, manual "Reset session" button, and fetchUser 401/403.
 * NOT called by arbitrary 401s from random endpoints.
 *
 * @param {string} where - caller identifier for diagnostic logging
 */
const purgeAuthState = (where = 'unknown') => {
    // eslint-disable-next-line no-console
    console.warn(`[Auth purge] called from: ${where}`);
    AUTH_STORAGE_KEYS.localStorage.forEach((k) => {
        try { localStorage.removeItem(k); } catch { /* private browsing */ }
    });
    AUTH_STORAGE_KEYS.sessionStorage.forEach((k) => {
        try { sessionStorage.removeItem(k); } catch { /* private browsing */ }
    });
    delete axios.defaults.headers.common['Authorization'];
};

// ── Global axios request interceptor ──────────────────────────
// Cache-busting on GET requests + URL rewriter that defends against the recurring
// REACT_APP_BACKEND_URL regression. Rule: in this app frontend and backend are
// always served from the same host via Kubernetes ingress. The only legitimate
// cross-origin case is local dev (localhost / 127.0.0.1) AND legitimate external
// services we explicitly PUT/POST to (S3 presigned URLs, Stripe, etc.). Any other
// absolute URL pointing to a stale backend host is rewritten to a same-origin
// relative path so cookies/JWT and CORS work regardless of the .env value.
//
// The rewriter is INTENTIONALLY scoped to URLs whose host matches one of our
// known backend hosts (current REACT_APP_BACKEND_URL or its preview/prod
// counterparts). External hosts — S3, AWS, Stripe, Discogs, etc. — pass through
// untouched so chunked uploads can PUT directly to s3.amazonaws.com.
const KNOWN_BACKEND_HOST_PATTERNS = [
    /\.preview\.emergentagent\.com$/i,
    /\.emergent\.host$/i,
    /^flow\.owntune\.com$/i,
];
if (!axios.__owntune_interceptors_set) {
    axios.interceptors.request.use((config) => {
        let isExternalHost = false;
        try {
            if (typeof window !== 'undefined' && config.url && /^https?:\/\//i.test(config.url)) {
                const u = new URL(config.url);
                const isLocalDev = /^(localhost|127\.0\.0\.1|\[::1\])$/i.test(u.hostname);
                const isKnownBackend = KNOWN_BACKEND_HOST_PATTERNS.some(rx => rx.test(u.hostname));
                // Only rewrite if URL points at a *stale* backend host (matches
                // a known emergent/owntune pattern but is NOT the current
                // window host). Never rewrite arbitrary external hosts like S3.
                if (!isLocalDev && isKnownBackend && u.host !== window.location.host) {
                    config.url = u.pathname + u.search + u.hash;
                }
                // S3 presigned URLs are sigv4-signed: appending a `_t` cache
                // buster would invalidate the signature. Mark external hosts
                // so we skip cache-busting below.
                isExternalHost = !isLocalDev && !isKnownBackend && u.host !== window.location.host;
            }
        } catch { /* fall through with original URL */ }

        if (config.method === 'get' && !isExternalHost) {
            config.params = { ...config.params, _t: Date.now() };
        }
        return config;
    });
    axios.__owntune_interceptors_set = true;
}

export { purgeAuthState, AUTH_STORAGE_KEYS };

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    
    // Impersonation state
    const [isImpersonating, setIsImpersonating] = useState(false);
    const [impersonationInfo, setImpersonationInfo] = useState(null);
    const [originalAdminToken, setOriginalAdminToken] = useState(
        sessionStorage.getItem('originalAdminToken')
    );
    
    // Company switching state
    const [switchingCompany, setSwitchingCompany] = useState(false);

    const fetchUser = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`);
            
            // Validate response shape — a CDN/proxy returning a cached HTML page
            // will not have the expected fields
            if (!response.data || typeof response.data !== 'object' || !response.data.id || !response.data.email) {
                console.warn('[Auth] /auth/me returned invalid data — clearing token');
                purgeAuthState('fetchUser:invalid-response-shape');
                setToken(null);
                setUser(null);
                return;
            }
            
            // eslint-disable-next-line no-console
            console.log('[Auth] /auth/me → 200', { id: response.data.id, email: response.data.email, role: response.data.role });
            setUser(response.data);
            await checkImpersonationStatus();
        } catch (error) {
            const status = error?.response?.status;
            console.error('[Auth] /auth/me failed:', status, error?.message);
            // Only purge on definitive auth failures (401/403), not on network/server errors
            if (status === 401 || status === 403) {
                purgeAuthState(`fetchUser:${status}`);
                setToken(null);
                setUser(null);
            } else {
                // Network error or 500 — keep the token, user might reconnect
                // But clear user so UI shows loading/login
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }

        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const checkImpersonationStatus = async () => {
        try {
            const response = await axios.get(`${API}/auth/impersonation-status`);
            if (response.data.is_impersonating) {
                setIsImpersonating(true);
                setImpersonationInfo({
                    impersonationId: response.data.impersonation_id,
                    originalAdminId: response.data.original_admin_id,
                    originalAdminName: response.data.original_admin_name,
                    readOnly: response.data.read_only,
                    targetUser: response.data.target_user
                });
            } else {
                setIsImpersonating(false);
                setImpersonationInfo(null);
            }
        } catch (error) {
            console.error('Failed to check impersonation status:', error);
            setIsImpersonating(false);
            setImpersonationInfo(null);
        }
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        setIsImpersonating(false);
        setImpersonationInfo(null);
        // Diagnostic: confirm token persisted to localStorage and attached to axios defaults
        // eslint-disable-next-line no-console
        console.log('[Auth login] ok', {
            token_in_localStorage: !!localStorage.getItem('token'),
            axios_auth_header: !!axios.defaults.headers.common['Authorization'],
            user_id: userData?.id,
            user_email: userData?.email,
            user_role: userData?.role,
        });
        return userData;
    };

    const register = async (email, password, name, extras = {}) => {
        // Attach first-touch acquisition signals if we captured them.
        const { consumeAcquisition } = await import('../lib/acquisition');
        const acq = consumeAcquisition();
        // ``extras`` may include ``partner_programme_slug`` when the
        // user reached us through ``/register/{slug}``. The backend
        // silently ignores unknown/inactive slugs so a stale landing
        // URL still lets them sign up as a plain customer.
        const response = await axios.post(`${API}/auth/register`, {
            email,
            password,
            name,
            ...acq,
            ...extras,
        });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const loginWithGoogle = async (sessionId) => {
        const response = await axios.post(`${API}/auth/google`, { session_id: sessionId });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        setIsImpersonating(false);
        setImpersonationInfo(null);
        return userData;
    };

    const loginWithGoogleOneTap = async (credential) => {
        const response = await axios.post(`${API}/auth/google-one-tap`, { credential });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        setUser(userData);
        setIsImpersonating(false);
        setImpersonationInfo(null);
        return userData;
    };

    const logout = () => {
        purgeAuthState('logout-button');
        setOriginalAdminToken(null);
        setIsImpersonating(false);
        setImpersonationInfo(null);
        setToken(null);
        setUser(null);
    };
    
    // Switch active company
    const switchCompany = async (companyId) => {
        if (switchingCompany) return;
        
        try {
            setSwitchingCompany(true);
            const response = await axios.post(`${API}/auth/switch-company`, {
                company_id: companyId
            });
            
            const { token: newToken, user: userData } = response.data;
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            setToken(newToken);
            setUser(userData);
            
            return userData;
        } catch (error) {
            console.error('Failed to switch company:', error);
            throw error;
        } finally {
            setSwitchingCompany(false);
        }
    };
    
    // Get all user's companies
    const companies = user?.companies || [];

    // Get active company info — enrich with logo_url + Affinity
    // Programme snapshot from the companies list so the Header /
    // Companies & Roles UI can render "Affinity Programme · Artisten"
    // (badge in profile dropdown, discount row on the company card)
    // without a second fetch.
    const activeCompany = user ? {
        id: user.active_company_id,
        name: user.active_company_name,
        role: user.active_company_role,
        logo_url: companies.find((c) => c.id === user.active_company_id)?.logo_url || null,
        affinity_programme: companies.find((c) => c.id === user.active_company_id)?.affinity_programme || null,
    } : null;
    
    // Check if user has multiple companies
    const hasMultipleCompanies = companies.length > 1;

    // Start impersonating a user
    const startImpersonation = async (targetUserId, readOnly = true, companyId = null) => {
        try {
            // Store current admin token
            const currentToken = localStorage.getItem('token');
            sessionStorage.setItem('originalAdminToken', currentToken);
            setOriginalAdminToken(currentToken);
            
            // Request impersonation token
            const requestBody = {
                target_user_id: targetUserId,
                read_only: readOnly
            };
            
            // Include company_id for multi-company users
            if (companyId) {
                requestBody.company_id = companyId;
            }
            
            const response = await axios.post(`${API}/admin/impersonate`, requestBody);
            
            const { token: impersonationToken, user: targetUser, impersonation_id, read_only, original_admin_name } = response.data;
            
            // Switch to impersonation token
            localStorage.setItem('token', impersonationToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${impersonationToken}`;
            setToken(impersonationToken);
            setUser(targetUser);
            
            // Set impersonation state
            setIsImpersonating(true);
            setImpersonationInfo({
                impersonationId: impersonation_id,
                originalAdminId: response.data.original_admin_id,
                originalAdminName: original_admin_name,
                readOnly: read_only,
                targetUser: targetUser
            });
            
            return targetUser;
        } catch (error) {
            // Restore original token on failure
            sessionStorage.removeItem('originalAdminToken');
            setOriginalAdminToken(null);
            throw error;
        }
    };

    // End impersonation and return to admin
    const endImpersonation = async () => {
        const savedAdminToken = sessionStorage.getItem('originalAdminToken') || originalAdminToken;
        
        if (!savedAdminToken) {
            console.error('No original admin token found');
            logout();
            return;
        }
        
        try {
            // End the impersonation session on backend
            if (impersonationInfo?.impersonationId) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${savedAdminToken}`;
                await axios.post(`${API}/admin/impersonate/${impersonationInfo.impersonationId}/end`);
            }
        } catch (error) {
            console.error('Failed to end impersonation on backend:', error);
        }
        
        // Restore admin token
        localStorage.setItem('token', savedAdminToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedAdminToken}`;
        setToken(savedAdminToken);
        
        // Clear impersonation state
        sessionStorage.removeItem('originalAdminToken');
        setOriginalAdminToken(null);
        setIsImpersonating(false);
        setImpersonationInfo(null);
        
        // Fetch admin user data
        await fetchUser();
    };

    const value = {
        user,
        setUser,  // Exposed so profile-picture / self-update flows can push a fresh /me response
        token,
        loading,
        login,
        register,
        loginWithGoogle,
        loginWithGoogleOneTap,
        logout,
        isAuthenticated: !!user,
        isAdmin: (user?.role === 'admin' || user?.role === 'super_admin') && !isImpersonating,
        isSuperAdmin: user?.role === 'super_admin' && !isImpersonating,
        isTranslator: user?.role === 'translator',
        isCustomer: user?.role === 'customer',
        canAccessTranslations: (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'translator') && !isImpersonating,
        // Permissions
        permissions: user?.permissions || [],
        hasPermission: (perm) => {
            if (isImpersonating) return false;
            if (user?.role === 'admin' || user?.role === 'super_admin') return true;
            return (user?.permissions || []).includes(perm);
        },
        // Impersonation
        isImpersonating,
        impersonationInfo,
        startImpersonation,
        endImpersonation,
        // Read-only check for impersonation
        isReadOnly: isImpersonating && impersonationInfo?.readOnly,
        // Multi-company support
        activeCompany,
        companies,
        hasMultipleCompanies,
        switchCompany,
        switchingCompany,
        // Helper: check if user is admin in active company
        isCompanyAdmin: user?.role === 'admin' || user?.role === 'super_admin' || 
                        user?.active_company_role === 'owner' || user?.active_company_role === 'admin',
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
